import { Parent_Type_Enum } from '@risksmart-app/web-graphql-client/generated/graphql';
import type { FC, ReactNode } from 'react';
import { ModalBodyWrapper } from 'src/components/form/form/ModalBodyWrapper';
import { PageWrapper } from 'src/components/form/form/PageWrapper';
import DocumentAssessmentResultForm from 'src/pages/assessments/forms/DocumentAssessmentResultForm';
import type { DocumentAssessmentResultFormDataFields } from 'src/pages/assessments/forms/documentAssessmentResultSchema';
import { defaultValues } from 'src/pages/assessments/forms/documentAssessmentResultSchema';
import { isResultFormReadOnly } from 'src/pages/assessments/forms/resultFormReadOnly';
import type { ObjectWithContributors } from 'src/rbac/Permission';
import { useHasPermissionQuery } from 'src/rbac/useHasPermission';

import { useMultiParentFileUpdate } from '@/hooks/mutations/file/useFileUpdate';
import { useInsertDocumentOversightResult } from '@/hooks/mutations/oversight-result/useInsertDocumentOversightResult';
import { useUpdateDocumentOversightResult } from '@/hooks/mutations/oversight-result/useUpdateDocumentOversightResult';
import { useGetDocumentOversightResultById } from '@/hooks/queries';
import { saveFilesForOptionalCreatedEntity } from '@/utils/createWithFiles';
import { oversightAssessmentResultsUrl } from '@/utils/urls';

type Props = {
  readonly: boolean;
  navigateToResults: boolean;
  isModalForm: boolean;
  parentId?: string;
  assessedItem?: ObjectWithContributors;
  id?: string;
  onDismiss?: (saved: boolean) => void;
  beforeFieldsSlot?: ReactNode;
  showAssessmentSelector?: boolean;
  documentIds?: string[];
  header?: string;
};

const ConnectedDocumentOversightResultForm: FC<Props> = ({
  readonly,
  parentId,
  assessedItem,
  id,
  onDismiss,
  beforeFieldsSlot,
  showAssessmentSelector,
  navigateToResults,
  isModalForm,
  documentIds,
  header,
}) => {
  const { data } = useGetDocumentOversightResultById({
    queryArgs: { id: id! },
    shouldSkip: !id,
  });
  const documentOversightResult = data?.document_oversight_result?.[0];
  const {
    hasPermission: canUpdateDocumentOversightResult,
    loading: isLoadingCanUpdateDocumentAssessmentResult,
  } = useHasPermissionQuery('update:document_oversight_result', assessedItem);

  const { updateFiles } = useMultiParentFileUpdate();
  const { insertDocumentOversightResult } = useInsertDocumentOversightResult();
  const { updateDocumentOversightResult } = useUpdateDocumentOversightResult();

  documentIds = documentIds ?? [];
  const parentDocument = documentOversightResult?.parents.find(
    (p) => p.document
  );
  if (parentDocument?.document?.Id) {
    documentIds.push(parentDocument.document.Id);
  } else if (assessedItem?.Id) {
    documentIds.push(assessedItem.Id);
  }

  const onSave = async (values: DocumentAssessmentResultFormDataFields) => {
    const { files } = values;
    const oversightAssessmentId =
      parentId || values.OversightAssessmentId || undefined;
    const documentOversightResultIds: string[] = [];
    if (documentOversightResult) {
      const result = await updateDocumentOversightResult({
        Rating: values.Rating,
        Rationale: values.Rationale,
        TestDate: values.TestDate,
        CustomAttributeData: values.CustomAttributeData ?? null,
        Id: documentOversightResult.Id,
      });

      if (result.update_document_oversight_result?.affected_rows === 0) {
        throw new Error('Document second line result update failed');
      }
      documentOversightResultIds.push(id!);
    } else {
      if (!oversightAssessmentId) {
        throw new Error('An oversight assessment is required');
      }
      const result = await insertDocumentOversightResult({
        OversightAssessmentId: oversightAssessmentId,
        DocumentIds: values?.DocumentIds?.map((r) => r.value) ?? [],
        Rating: values.Rating,
        Rationale: values.Rationale,
        TestDate: values.TestDate,
        CustomAttributeData: values.CustomAttributeData ?? null,
      });

      if (!result.insertChildDocumentOversightResult?.Ids?.length) {
        throw new Error('Document second line result id is missing');
      }
      documentOversightResultIds.push(
        ...result.insertChildDocumentOversightResult.Ids
      );
    }

    const savedId = documentOversightResultIds[0];
    if (!savedId) {
      throw new Error('Document second line result id is missing');
    }
    const createdEntity = !documentOversightResult
      ? {
          id: savedId,
          destination: oversightAssessmentResultsUrl(oversightAssessmentId),
        }
      : undefined;
    const saveFiles = () =>
      updateFiles({
        parentIds: documentOversightResultIds,
        parentType: Parent_Type_Enum.DocumentOversightResult,
        selectedFiles: files,
        originalFiles: documentOversightResult?.files.map((f) => f.file) ?? [],
      });
    await saveFilesForOptionalCreatedEntity({ createdEntity, saveFiles });

    return {
      savedIds: documentOversightResultIds,
      createdEntity,
      successNavigation:
        navigateToResults && oversightAssessmentId
          ? {
              destination: oversightAssessmentResultsUrl(oversightAssessmentId),
            }
          : undefined,
    };
  };

  const handleDismiss = (isSaved: boolean) => {
    onDismiss?.(isSaved);
  };

  return (
    <DocumentAssessmentResultForm
      header={header}
      defaultValues={{
        ...defaultValues,
        DocumentIds: documentIds.map((c) => ({ value: c })),
        OversightAssessmentId: parentId,
      }}
      values={
        documentOversightResult
          ? {
              Rating: documentOversightResult?.Rating ?? 1,
              Rationale: documentOversightResult?.Rationale ?? '',
              OversightAssessmentId: parentId,
              DocumentIds: documentIds.map((c) => ({ value: c })),
              TestDate: documentOversightResult?.TestDate,
              CustomAttributeData: documentOversightResult?.CustomAttributeData,
              files: documentOversightResult?.files.map((f) => f.file) ?? [],
            }
          : undefined
      }
      onDismiss={handleDismiss}
      onSave={onSave}
      readOnly={isResultFormReadOnly({
        readonly,
        permissionLoading: isLoadingCanUpdateDocumentAssessmentResult,
        canUpdate: canUpdateDocumentOversightResult,
      })}
      renderTemplate={(renderProps) =>
        isModalForm ? (
          <ModalBodyWrapper {...renderProps} />
        ) : (
          <PageWrapper {...renderProps} />
        )
      }
      disableDocumentSelector={id != undefined}
      beforeFieldsSlot={beforeFieldsSlot}
      showSelector={showAssessmentSelector ? 'oversight_assessment' : undefined}
      assessmentMode={'oversight_assessment'}
    />
  );
};

export default ConnectedDocumentOversightResultForm;
